<template>
  <span class="status-chip" :class="['tone-' + tone, { 'status-chip--small': small }]">
    <v-icon v-if="icon" class="status-chip__icon" aria-hidden="true">{{ icon }}</v-icon>
    <span v-else class="status-chip__dot" aria-hidden="true"></span>{{ status || "—" }}
  </span>
</template>

<script>
// One colour per phase of a request: issued (sky), in the approval chain (teal), being done (amber),
// done (green), rejected (brick), impossible for now / cancelled (slate). A logged data change (plain) and a
// comment (plum) are not phases. Works on the response_types text.
export function statusTone(status) {
  const s = (status || "").toLowerCase();
  if (!s) return "slate";
  if (s.startsWith("respins")) return "brick";
  if (s.startsWith("îndeplinit") || s.startsWith("indeplinit")) return "green";
  if (s.startsWith("anulat")) return "slate";
  if (s.startsWith("date modificate")) return "plain";
  // a comment at any time (008): not a phase either
  if (s.startsWith("comentariu")) return "plum";
  // the author's correction sends the request back to the Șef: issued again
  if (s.startsWith("corectat")) return "sky";
  if (s.includes("proces") || s.includes("discre") || s.includes("lucru")) return "amber";
  if (s.startsWith("imposibil")) return "slate";
  if (s.startsWith("emis")) return "sky";
  if (s.startsWith("aprobat")) return "teal";
  return "slate";
}

// Steps that are not part of the normal chain get an icon instead of the dot
export function statusIcon(status) {
  const s = (status || "").toLowerCase();
  if (s.startsWith("anulat")) return "mdi-cancel";
  if (s.startsWith("corectat")) return "mdi-file-edit-outline";
  if (s.startsWith("date modificate")) return "mdi-pencil-outline";
  if (s.startsWith("comentariu")) return "mdi-comment-text-outline";
  return null;
}

// The tone as a CSS colour (for v-timeline dots and the like)
export function statusColor(status) {
  const tone = statusTone(status);
  return tone === "plain" ? "var(--ink-3)" : `var(--${tone})`;
}

export default {
  name: "StatusChip",
  props: {
    status: { type: String, default: "" },
    small: { type: Boolean, default: false },
  },
  computed: {
    tone() {
      return statusTone(this.status);
    },
    icon() {
      return statusIcon(this.status);
    },
  },
};
</script>

<style scoped>
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px 5px 10px;
  border-radius: 999px;
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.25;
  white-space: normal;
  max-width: 100%;
}
.status-chip--small {
  font-size: 0.78rem;
  padding: 2px 9px 2px 8px;
  gap: 6px;
}
.status-chip__dot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}
.status-chip .status-chip__icon.v-icon {
  flex: none;
  color: currentColor;
  font-size: 16px;
}
.status-chip--small .status-chip__icon.v-icon {
  font-size: 14px;
}
.tone-sky { color: var(--sky); background: var(--sky-bg); }
.tone-teal { color: var(--teal); background: var(--teal-bg); }
.tone-amber { color: var(--amber); background: var(--amber-bg); }
.tone-green { color: var(--green-700); background: #dcf1e4; }
.tone-brick { color: var(--brick); background: var(--brick-bg); }
.tone-slate { color: var(--slate); background: var(--slate-bg); }
.tone-plain { color: var(--ink-2); background: #fff; box-shadow: inset 0 0 0 1px var(--line); }
.tone-plum { color: var(--plum); background: var(--plum-bg); }
</style>
